import type { GeoJSONFeature } from "mapbox-gl";
import {
  BufferAttribute,
  BufferGeometry,
  Matrix4,
  Mesh,
  Raycaster,
  Vector3,
} from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import mapboxgl from "mapbox-gl";

export interface LocalOrigin {
  lng: number;
  lat: number;
}

/**
 * Local meters aligned with Mapbox `meterInMercatorCoordinateUnits()`:
 * X east, Y north (simshady ENU).
 */
export function lngLatToLocalMeters(
  lng: number,
  lat: number,
  origin: LocalOrigin
): { x: number; y: number } {
  const originMerc = mapboxgl.MercatorCoordinate.fromLngLat([
    origin.lng,
    origin.lat,
  ]);
  const pointMerc = mapboxgl.MercatorCoordinate.fromLngLat([lng, lat]);
  const metersPerUnit = originMerc.meterInMercatorCoordinateUnits();
  return {
    x: (pointMerc.x - originMerc.x) / metersPerUnit,
    y: -(pointMerc.y - originMerc.y) / metersPerUnit,
  };
}

function ringToLocalMeters(
  ring: number[][],
  origin: LocalOrigin
): Array<[number, number]> {
  return ring.map(([lng, lat]) => {
    const local = lngLatToLocalMeters(lng, lat, origin);
    return [local.x, local.y] as [number, number];
  });
}

function getOpenRing(ring: number[][]): number[][] {
  if (ring.length < 2) return ring;
  const [fLng, fLat] = ring[0];
  const [lLng, lLat] = ring[ring.length - 1];
  if (fLng === lLng && fLat === lLat) {
    return ring.slice(0, -1);
  }
  return ring;
}

/** Signed area in local XY; positive = counter-clockwise (roof normal +Z). */
function ringSignedArea2D(ring: Array<[number, number]>): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x0, y0] = ring[i];
    const [x1, y1] = ring[(i + 1) % ring.length];
    sum += x0 * y1 - x1 * y0;
  }
  return sum * 0.5;
}

function ensureCounterClockwise(
  ring: Array<[number, number]>,
): Array<[number, number]> {
  if (ring.length < 3) return ring;
  return ringSignedArea2D(ring) < 0 ? [...ring].reverse() : ring;
}

/** Horizontal cap with +Z normal (simshady keeps upward-facing triangles). */
function triangulateHorizontalCap(
  ring: Array<[number, number]>,
  z: number,
): number[] {
  if (ring.length < 3) return [];

  const triangles: number[] = [];
  for (let i = 1; i < ring.length - 1; i++) {
    const [x0, y0] = ring[0];
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[i + 1];
    triangles.push(x0, y0, z, x1, y1, z, x2, y2, z);
  }
  return triangles;
}

function extrudeFootprint(
  ring: Array<[number, number]>,
  height: number
): number[] {
  if (ring.length < 3 || height <= 0) return [];

  const footprint = ensureCounterClockwise(ring);
  const positions: number[] = [];
  const n = footprint.length;

  for (let i = 0; i < n; i++) {
    const [x0, y0] = footprint[i];
    const [x1, y1] = footprint[(i + 1) % n];

    positions.push(
      x0,
      y0,
      0,
      x1,
      y1,
      0,
      x1,
      y1,
      height,
      x0,
      y0,
      0,
      x1,
      y1,
      height,
      x0,
      y0,
      height
    );
  }

  positions.push(...triangulateHorizontalCap(footprint, height));

  return positions;
}

export function getFeatureHeight(feature: GeoJSONFeature): number {
  const props = feature.properties ?? {};
  return Number(
    props.height ?? props.render_height ?? props.extrude ?? props["building:height"] ?? 0
  );
}

function getFeatureRing(feature: GeoJSONFeature): number[][] | null {
  const geometry = feature.geometry;
  if (!geometry) return null;

  if (geometry.type === "Polygon") {
    return geometry.coordinates[0] as number[][];
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates[0]?.[0] as number[][] | undefined ?? null;
  }

  return null;
}

export function normalizeGeometryPositionsOnly(
  geometry: BufferGeometry
): BufferGeometry {
  const nonIndexed =
    geometry.index !== null ? geometry.toNonIndexed() : geometry.clone();
  const normalized = new BufferGeometry();
  normalized.setAttribute(
    "position",
    nonIndexed.getAttribute("position").clone()
  );
  nonIndexed.dispose();
  return normalized;
}

export function mergePositionGeometries(
  geometries: BufferGeometry[]
): BufferGeometry | null {
  if (geometries.length === 0) return null;

  const normalized = geometries.map((geometry) =>
    normalizeGeometryPositionsOnly(geometry)
  );
  const merged = mergeGeometries(normalized, false);
  normalized.forEach((geometry) => geometry.dispose());
  return merged;
}

export function featuresToBufferGeometry(
  features: GeoJSONFeature[],
  origin: LocalOrigin
): BufferGeometry | null {
  const geometries: BufferGeometry[] = [];

  for (const feature of features) {
    const ring = getFeatureRing(feature);
    const height = getFeatureHeight(feature);
    if (!ring || ring.length < 3 || height <= 0) continue;

    const localRing = ensureCounterClockwise(
      ringToLocalMeters(getOpenRing(ring), origin),
    );
    const positions = extrudeFootprint(localRing, height);
    if (positions.length === 0) continue;

    const geometry = new BufferGeometry();
    geometry.setAttribute(
      "position",
      new BufferAttribute(new Float32Array(positions), 3)
    );
    geometries.push(geometry.toNonIndexed());
  }

  if (geometries.length === 0) return null;
  const merged = mergePositionGeometries(geometries);
  geometries.forEach((geometry) => geometry.dispose());
  return merged;
}

export function featuresToShadingMeshes(
  features: GeoJSONFeature[],
  origin: LocalOrigin
): Mesh[] {
  const geometry = featuresToBufferGeometry(features, origin);
  if (!geometry) return [];

  const mesh = new Mesh(geometry);
  mesh.updateMatrixWorld(true);
  return [mesh];
}

export function sunDirectionFromPosition(
  altitudeRad: number,
  azimuthRad: number
): Vector3 {
  const altitude = altitudeRad;
  const azimuth = azimuthRad + Math.PI;
  return new Vector3(
    Math.cos(altitude) * Math.sin(azimuth),
    Math.cos(altitude) * Math.cos(azimuth),
    Math.sin(altitude)
  ).normalize();
}

export function isPointInSun(
  meshes: Mesh[],
  origin: LocalOrigin,
  lng: number,
  lat: number,
  altitudeRad: number,
  azimuthRad: number,
  pointHeight = 0.1
): boolean {
  if (altitudeRad <= 0) return false;

  const local = lngLatToLocalMeters(lng, lat, origin);
  const originPoint = new Vector3(local.x, local.y, pointHeight);
  const sunDirection = sunDirectionFromPosition(altitudeRad, azimuthRad);
  const raycaster = new Raycaster(
    originPoint,
    sunDirection,
    0,
    5000
  );

  for (const mesh of meshes) {
  const hits = raycaster.intersectObject(mesh, true);
    if (hits.length > 0) return false;
  }

  return true;
}

export function sunCalcToMapboxDirection(
  altitudeRad: number,
  azimuthRad: number
): [number, number] {
  const altitudeDeg = (altitudeRad * 180) / Math.PI;
  const azimuthFromNorth =
    (((azimuthRad * 180) / Math.PI + 180) % 360 + 360) % 360;
  const polar = Math.max(0, Math.min(90, 90 - altitudeDeg));
  return [azimuthFromNorth, polar];
}

export function getIrradianceUrl(lat: number, lng: number): string {
  const roundedLat = (Math.round(lat * 10) / 10).toFixed(1);
  const roundedLng = (Math.round(lng * 10) / 10).toFixed(1);
  return `https://api.openpv.de/skymaps/irradiance_${roundedLat}_${roundedLng}_2018_yearly.json`;
}

export function createHorizontalPatchGeometry(
  lng: number,
  lat: number,
  origin: LocalOrigin,
  sizeMeters = 1
): BufferGeometry {
  const center = lngLatToLocalMeters(lng, lat, origin);
  return createSimulationPatchGeometry(origin, sizeMeters, center);
}

export function createSimulationPatchGeometry(
  _origin: LocalOrigin,
  sizeMeters: number,
  center: { x: number; y: number } = { x: 0, y: 0 },
  elevationMeters = 0.5
): BufferGeometry {
  const half = sizeMeters / 2;
  const z = elevationMeters;
  const positions = new Float32Array([
    center.x - half,
    center.y - half,
    z,
    center.x + half,
    center.y - half,
    z,
    center.x + half,
    center.y + half,
    z,
    center.x - half,
    center.y - half,
    z,
    center.x + half,
    center.y + half,
    z,
    center.x - half,
    center.y + half,
    z,
  ]);

  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(positions, 3));
  return geometry;
}

export function getMapViewportPatchSizeMeters(
  map: mapboxgl.Map,
  minMeters = 80,
  maxMeters = 400
): number {
  const bounds = map.getBounds();
  if (!bounds) {
    return minMeters;
  }
  const center = map.getCenter();
  const origin: LocalOrigin = { lng: center.lng, lat: center.lat };
  const ne = lngLatToLocalMeters(
    bounds.getNorthEast().lng,
    bounds.getNorthEast().lat,
    origin
  );
  const sw = lngLatToLocalMeters(
    bounds.getSouthWest().lng,
    bounds.getSouthWest().lat,
    origin
  );
  const span = Math.max(Math.abs(ne.x - sw.x), Math.abs(ne.y - sw.y));
  return Math.max(minMeters, Math.min(maxMeters, span * 0.85));
}

export function getFeaturesOrigin(
  features: GeoJSONFeature[],
): LocalOrigin | null {
  let lng = 0;
  let lat = 0;
  let count = 0;

  for (const feature of features) {
    const geometry = feature.geometry;
    if (!geometry) continue;

    if (geometry.type === "Point") {
      const [pointLng, pointLat] = geometry.coordinates as [number, number];
      lng += pointLng;
      lat += pointLat;
      count++;
      continue;
    }

    const ring = getFeatureRing(feature);
    if (!ring) continue;
    for (const [ringLng, ringLat] of ring) {
      lng += ringLng;
      lat += ringLat;
      count++;
    }
  }

  if (count === 0) return null;
  return { lng: lng / count, lat: lat / count };
}

export function getFeaturesPatchSizeMeters(
  features: GeoJSONFeature[],
  origin: LocalOrigin,
  minMeters = 40,
  maxMeters = 400
): number {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const feature of features) {
    const ring = getFeatureRing(feature);
    if (!ring) continue;
    for (const [lng, lat] of ring) {
      const local = lngLatToLocalMeters(lng, lat, origin);
      minX = Math.min(minX, local.x);
      minY = Math.min(minY, local.y);
      maxX = Math.max(maxX, local.x);
      maxY = Math.max(maxY, local.y);
    }
  }

  if (!Number.isFinite(minX)) {
    return minMeters;
  }

  const span = Math.max(maxX - minX, maxY - minY, minMeters);
  return Math.min(maxMeters, span * 1.25);
}

export function applyLocalShift(
  geometry: BufferGeometry,
  shift: Vector3
): void {
  const positions = geometry.attributes.position.array as Float32Array;
  for (let i = 0; i < positions.length; i += 3) {
    positions[i] -= shift.x;
    positions[i + 1] -= shift.y;
    positions[i + 2] -= shift.z;
  }
  geometry.attributes.position.needsUpdate = true;
}

export function getGeometryShift(geometry: BufferGeometry): Vector3 {
  const positions = geometry.attributes.position.array as Float32Array;
  return new Vector3(positions[0], positions[1], positions[2]);
}

export function restoreLocalShift(
  geometry: BufferGeometry,
  shift: Vector3
): void {
  const positions = geometry.attributes.position.array as Float32Array;
  for (let i = 0; i < positions.length; i += 3) {
    positions[i] += shift.x;
    positions[i + 1] += shift.y;
    positions[i + 2] += shift.z;
  }
  geometry.attributes.position.needsUpdate = true;
}
export function transformMeshesToRaycastSpace(
  meshes: Mesh[],
  shift: Vector3
): Mesh[] {
  const matrix = new Matrix4().makeTranslation(-shift.x, -shift.y, -shift.z);
  return meshes.map((mesh) => {
    const cloned = mesh.clone();
    cloned.geometry = mesh.geometry.clone();
    cloned.geometry.applyMatrix4(matrix);
    cloned.updateMatrixWorld(true);
    return cloned;
  });
}
